/**
 * Some B1Link services (e.g. COI) serialize UTC `DateTime` columns WITHOUT a
 * timezone marker — EF reads SQL `datetime2` as Kind=Unspecified and
 * System.Text.Json then omits the trailing `Z`. `new Date("2026-07-08T00:36:00")`
 * parses such naive strings as LOCAL time, shifting every timestamp by the
 * client's UTC offset (2h in SAST). Assume any zone-less ISO datetime is UTC and
 * append `Z` before parsing. Idempotent: strings that already carry `Z` or a
 * numeric offset, Date objects, and date-only strings are left untouched.
 */
export const parseApiDate = (date: string | Date): Date => {
  if (typeof date !== 'string') return new Date(date);
  // Has a time component (T…) but no zone (Z / ±hh:mm) -> treat as UTC.
  const zoneless = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(date);
  return new Date(zoneless ? `${date}Z` : date);
};

/**
 * #7085: is this value .NET's default/min date rather than a real one?
 *
 * Services that expose a NON-nullable `DateTime`/`DateTimeOffset` serialize "no value" as
 * `0001-01-01T00:00:00`. That parses as a valid — and very past — date, so naive consumers render
 * it ("01 Jan 0001") and compare against it ("expired years ago"). B-BBEE Master Data hit both:
 * suppliers with no certificate showed a red "Expired" badge (#7085).
 *
 * The indexer no longer writes the sentinel, but documents indexed BEFORE that fix still carry it
 * until a reindex — and other services still expose non-nullable dates — so display and comparison
 * paths must stay sentinel-tolerant. Absent (null/undefined/'') is NOT a sentinel: callers already
 * guard falsy separately, and conflating the two would hide genuine "no value" handling.
 */
export const isSentinelDate = (date?: string | Date | null): boolean => {
  if (!date) return false;

  const parsed = parseApiDate(date);
  if (Number.isNaN(parsed.getTime())) return false;

  // `<= 1` rather than an exact epoch match: the sentinel arrives with and without a zone marker,
  // and a positive offset (e.g. `0001-01-01T00:00:00+02:00`, SAST) shifts it back into year 0.
  return parsed.getUTCFullYear() <= 1;
};

// Shared en-ZA display formatters (deterministic, no Intl for dates) live in @b1link/ui. They are
// re-exported here under non-clashing names for new code; the exports below are unchanged.
export {
  formatDate as formatDateZA,
  formatDateTime as formatDateTimeZA,
  formatRelative
} from '@b1link/ui';

// Format date as YYYY-MM-DD (for form submissions and API)
const formatDate = (date: string | Date) => {
  if (!date) return '';

  try {
    const dt = parseApiDate(date);
    if (Number.isNaN(dt.getTime())) return '';

    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(dt);
  } catch {
    return '';
  }
};

export default formatDate;

export const formatDateDisplay = (date: string | Date | null | undefined): string => {
  if (!date) return '';

  try {
    const dt = parseApiDate(date);
    if (Number.isNaN(dt.getTime())) return '';
    // #7085: a min-date sentinel is "no date", not "1 Jan 1".
    if (isSentinelDate(dt)) return '';

    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(dt);
  } catch {
    return '';
  }
};

/**
 * #8604: display a CALENDAR date (a date with no time-of-day meaning — CIPC
 * appointment/registration dates, register start/appointed/DOB dates) as
 * "1 Aug 2012".
 *
 * Unlike `formatDateDisplay`, this formats in UTC. APIs send these values as
 * date-only (`2012-08-01`) or midnight-UTC (`2012-08-01T00:00:00Z`) strings, so a
 * local-time format would move the day back for any viewer west of UTC.
 */
export const formatCalendarDateDisplay = (
  date: string | Date | null | undefined
): string => {
  if (!date) return '';

  // A calendar date is the day it was written as, whatever offset rides along
  // ('2017-04-03T00:00:00+02:00' is 3 Apr, not the UTC instant's 2 Apr).
  const writtenDay =
    typeof date === 'string' ? /^\d{4}-\d{2}-\d{2}/.exec(date)?.[0] : undefined;
  const dt = writtenDay ? new Date(`${writtenDay}T00:00:00Z`) : parseApiDate(date);
  if (Number.isNaN(dt.getTime()) || isSentinelDate(dt)) return '';

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(dt);
};

/*
 * #8604: calendar-date INPUT helpers. A calendar date is stored and sent as a plain
 * ISO `YYYY-MM-DD` string and typed/displayed as `yyyy/mm/dd`. Conversions to and from
 * the date picker's `Date` use LOCAL date parts — never `toISOString()`, which turns a
 * SAST local midnight into the previous day.
 */
const CALENDAR_DATE_INPUT = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/;

const isoCalendarDate = (year: number, month: number, day: number): string | null => {
  const dt = new Date(Date.UTC(year, month - 1, day));
  const real =
    dt.getUTCFullYear() === year &&
    dt.getUTCMonth() === month - 1 &&
    dt.getUTCDate() === day;
  if (!real) return null;
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * Typed text -> stored value. A complete, real `yyyy/mm/dd` (or `yyyy-mm-dd`) date becomes
 * `YYYY-MM-DD`; blank becomes ''; anything else is returned as typed so validation can
 * reject it rather than the date being silently dropped.
 */
export const normaliseCalendarDateInput = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed) return '';
  const match = CALENDAR_DATE_INPUT.exec(trimmed);
  if (!match) return trimmed;
  return isoCalendarDate(Number(match[1]), Number(match[2]), Number(match[3])) ?? trimmed;
};

/** Is this a stored calendar date — a real date in ISO `YYYY-MM-DD` form? */
export const isCalendarDateValue = (value: string | null | undefined): boolean => {
  if (!value) return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return (
    !!match &&
    isoCalendarDate(Number(match[1]), Number(match[2]), Number(match[3])) !== null
  );
};

/** Stored value -> input text: `YYYY-MM-DD` (or an API midnight timestamp) shows as `yyyy/mm/dd`. */
export const formatCalendarDateInput = (value: string | null | undefined): string => {
  if (!value) return '';
  const datePart = value.slice(0, 10);
  return isCalendarDateValue(datePart) ? datePart.replaceAll('-', '/') : value;
};

/** A date picked in the calendar (local midnight) -> `YYYY-MM-DD`, using its LOCAL day. */
export const calendarDateFromLocalDate = (date: Date): string =>
  isoCalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate()) ?? '';

/** `YYYY-MM-DD` -> a local-midnight Date for the calendar; undefined when not a calendar date. */
export const localDateFromCalendarDate = (
  value: string | null | undefined
): Date | undefined => {
  if (!value || !isCalendarDateValue(value.slice(0, 10))) return undefined;
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const formatDateTime = (date: string | Date | null | undefined) => {
  if (!date) return '';

  try {
    const dt = parseApiDate(date);
    if (Number.isNaN(dt.getTime())) return '';

    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(dt);
  } catch {
    return '';
  }
};

/**
 * Compact relative-time label ("just now", "5 min ago", "2 h ago",
 * "3 d ago", "2 mo ago") for provenance / freshness microlines. Uses
 * `Intl.RelativeTimeFormat` (no dependency). Returns '' for missing /
 * unparseable input so callers can fall back to an absolute string.
 * `now` is injectable for deterministic unit tests.
 *
 * `allowFuture` (default false): the default clamps any future offset to
 * "just now" (see #8699 below). A caller whose value can LEGITIMATELY be in the
 * future — a registry date rather than an event stamp, e.g. a CIPC
 * appointed/resigned date in CompanyNetworkGraph — opts in with
 * `{ allowFuture: true }` and gets the normal "in 1 d" wording instead.
 */
export const formatRelativeTime = (
  date: string | Date | null | undefined,
  now: Date = new Date(),
  options?: { allowFuture?: boolean }
): string => {
  if (!date) return '';

  try {
    const dt = parseApiDate(date);
    if (Number.isNaN(dt.getTime())) return '';

    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto', style: 'short' });
    const diffSeconds = Math.round((dt.getTime() - now.getTime()) / 1000);
    const absSeconds = Math.abs(diffSeconds);

    // #8699: almost every caller renders an already-occurred event (createdAt,
    // completedAt, resolvedAt, submittedAt, syncedAt…), so a timestamp at or
    // after `now` can only be client/server clock skew — a just-created note
    // whose server clock ran 2 min ahead must read "just now", never the
    // nonsensical "in 2 min.". Collapse the entire future side to "just now"
    // rather than clamp an arbitrary skew window (any window just relocates the
    // same bug one second past its edge). The exceptions are the genuine
    // registry-date callers (CIPC appointed/resigned), which pass allowFuture so
    // a forward-dated appointment still reads "in 1 d".
    if (!options?.allowFuture && diffSeconds >= 0) return 'just now';

    if (absSeconds < 45) return 'just now';

    const divisions: {
      limit: number;
      seconds: number;
      unit: Intl.RelativeTimeFormatUnit;
    }[] = [
      { limit: 60 * 60, seconds: 60, unit: 'minute' },
      { limit: 60 * 60 * 24, seconds: 60 * 60, unit: 'hour' },
      { limit: 60 * 60 * 24 * 30, seconds: 60 * 60 * 24, unit: 'day' },
      { limit: 60 * 60 * 24 * 365, seconds: 60 * 60 * 24 * 30, unit: 'month' },
      { limit: Infinity, seconds: 60 * 60 * 24 * 365, unit: 'year' }
    ];

    const division =
      divisions.find((d) => absSeconds < d.limit) ?? divisions[divisions.length - 1];
    return rtf.format(Math.round(diffSeconds / division.seconds), division.unit);
  } catch {
    return '';
  }
};
